import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const USER_API_URL = "auth/admin/user-list/";
const PROFILE_API_URL = "agency/get-agency-profile/";
const PRODUCTS_API_URL = "agency/admin/create-products/";
const AREACODES_API_URL = "agency/admin/create-areacodes/";
const BANK_AREACODES_API_URL = "portfolio/agency-banks-area-codes/";
const REGIONS_API_URL = "agency/regions/";

export const fetchAgencyUsers = createAsyncThunk(
  "agency/fetchAgencyUsers",
  async (params, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const token = state.auth.token;
      if (!token) {
        return;
      }

      const response = await axios.get(USER_API_URL, {
        params,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch users list"
      );
    }
  }
);

export const fetchAgencyProfile = createAsyncThunk(
  "agency/fetchAgencyProfile",
  async (params, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const token = state.auth.token;
      if (!token) {
        return;
      }

      const user = state.auth.user;
      const isTelecaller = user?.is_tc;
      const isSupervisor = user?.is_supervisor;
      const apiUrl = isTelecaller ? "telecaller/tc-profile/" : (isSupervisor ? "agency/supervisor-profile/" : PROFILE_API_URL);

      const response = await axios.get(apiUrl, {
        params,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch users list"
      );
    }
  }
);

export const fetchAgencyProducts = createAsyncThunk(
  "agency/fetchAgencyProducts",
  async (params, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const token = state.auth.token;
      const validUser = state.auth.user;
      if (!token || (!validUser?.is_agency && !validUser?.is_supervisor)) {
        return;
      }

      const response = await axios.get(PRODUCTS_API_URL, {
        params,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch products"
      );
    }
  }
);
export const fetchAgencyProductsTable = createAsyncThunk(
  "agency/fetchAgencyProductsTable",
  async (params, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const token = state.auth.token;
      if (!token) {
        return;
      }

      const response = await axios.get(PRODUCTS_API_URL, {
        params,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch products"
      );
    }
  }
);

export const fetchAreaCodes = createAsyncThunk(
  "agency/fetchAreaCodes",
  async (params, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const token = state.auth.token;
      if (!token) {
        return;
      }

      const response = await axios.get(AREACODES_API_URL, {
        params,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch area codes"
      );
    }
  }
);
export const fetchBankAreaCodes = createAsyncThunk(
  "agency/fetchBankAreaCodes",
  async (params, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const token = state.auth.token;
      if (!token) {
        return;
      }

      const response = await axios.get(BANK_AREACODES_API_URL, {
        params,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch area codes"
      );
    }
  }
);

export const fetchRegions = createAsyncThunk(
  "agency/fetchRegions",
  async (params, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const token = state.auth.token;
      if (!token) {
        return;
      }

      const response = await axios.get(REGIONS_API_URL, {
        params,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch regions list"
      );
    }
  }
);

const initialState = {
  user_list: [],
  user_loading: false,
  error: null,
  agency_profile: [],
  profile_loading: false,
  products_list: [],
  products_loading: false,
  area_codes_list: [],
  area_codes_loading: false,

  regions_list: [],
  regions_loading: false,

  bank_area_codes: [],
  bank_area_codes_loading: false,

  product_list_table: [],
  product_list_table_loading: false
};

const agencySlice = createSlice({
  name: "agency",
  initialState,
  reducers: {
    clearUserList: (state)=>{
      state.user_list = [];
    }
  },
  extraReducers: (builder) => {
    builder

      // agency users 
      .addCase(fetchAgencyUsers.pending, (state) => {
        state.user_loading = true;
        state.error = null;
      })
      .addCase(fetchAgencyUsers.fulfilled, (state, action) => {
        state.user_loading = false;
        state.user_list = action.payload;
      })
      .addCase(fetchAgencyUsers.rejected, (state, action) => {
        state.user_loading = false;
        state.error = action.payload || "Something went wrong";
        state.user_list = [];
      })

      // agency profile 

      .addCase(fetchAgencyProfile.pending, (state) => {
        state.agency_profile = true;
        state.error = null;
      })
      .addCase(fetchAgencyProfile.fulfilled, (state, action) => {
        state.profile_loading = false;
        state.agency_profile = action.payload;
      })
      .addCase(fetchAgencyProfile.rejected, (state, action) => {
        state.profile_loading = false;
        state.error = action.payload || "Something went wrong";
        state.agency_profile = [];
      })

      // agency products
      .addCase(fetchAgencyProducts.pending, (state) => {
        state.products_loading = true;
        state.error = null;
      })
      .addCase(fetchAgencyProducts.fulfilled, (state, action) => {
        state.products_loading = false;
        state.products_list = action.payload;
      })
      .addCase(fetchAgencyProducts.rejected, (state, action) => {
        state.products_loading = false;
        state.error = action.payload || "Something went wrong";
        state.products_list = [];
      })



       .addCase(fetchAgencyProductsTable.pending, (state) => {
        state.product_list_table_loading = true;
        // state.error = null;
      })
      .addCase(fetchAgencyProductsTable.fulfilled, (state, action) => {
        state.product_list_table_loading = false;
        state.product_list_table = action.payload;
      })
      .addCase(fetchAgencyProductsTable.rejected, (state, action) => {
        state.product_list_table_loading = false;
        state.error = action.payload || "Something went wrong";
        state.product_list_table = [];
      })


      // area code 

      .addCase(fetchAreaCodes.pending, (state) => {
        state.area_codes_loading = true;
        state.error = null;
      })
      .addCase(fetchAreaCodes.fulfilled, (state, action) => {
        state.area_codes_loading = false;
        state.area_codes_list = action.payload;
      })
      .addCase(fetchAreaCodes.rejected, (state, action) => {
        state.area_codes_loading = false;
        state.error = action.payload || "Something went wrong";
        state.area_codes_list = [];
      })

      // regions 
      .addCase(fetchRegions.pending, (state) => {
        state.regions_loading = true;

      })
      .addCase(fetchRegions.fulfilled, (state, action) => {
        state.regions_loading = false;
        state.regions_list = action.payload;
      })
      .addCase(fetchRegions.rejected, (state, action) => {
        state.regions_loading = false;

        state.regions_list = [];
      })

      // bank area codes data 
      .addCase(fetchBankAreaCodes.pending, (state) => {
        state.bank_area_codes_loading = true;

      })
      .addCase(fetchBankAreaCodes.fulfilled, (state, action) => {
        state.bank_area_codes_loading = false;
        state.bank_area_codes = action.payload;
      })
      .addCase(fetchBankAreaCodes.rejected, (state, action) => {
        state.bank_area_codes_loading = false;

        state.bank_area_codes = [];
      })
  },
});

export const { clearUserList } = agencySlice.actions;
export default agencySlice.reducer;
